import React, { useState } from 'react';
import { Plus, Trash2, Zap, PiggyBank, ChevronDown } from 'lucide-react';
import { formatMoney, formatMoneyShort, currencies } from '../lib/currency';
import { monthsToLabel } from '../mock';
import { useAppSettings } from '../lib/i18n';
import { MiniChartCard } from './AdvancedSection';

export default function PrepaymentPanel({
  prepayments,
  setPrepayments,
  extraEmi,
  setExtraEmi,
  baseline,           // { schedule, summary } — with advance, WITHOUT prepayments
  current,            // full simulation.summary — with advance + prepayments
  currentFull,        // full simulation object (schedule + summary)
  chartType,
  loanAmount,
  moratoriumEnabled,
  moratoriumType,
  maxMonth,
}) {
  const { t, currency } = useAppSettings();
  const c = currencies[currency];
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(12);
  const [amount, setAmount] = useState(50000);

  const baselineSummary = baseline?.summary || {};
  const interestSaved = Math.max(0, (baselineSummary.totalInterest || 0) - (current?.totalInterest || 0));
  const monthsSaved = Math.max(0, (baselineSummary.monthsTaken || 0) - (current?.monthsTaken || 0));
  const hasPrepay = prepayments.length > 0 || extraEmi > 0;
  const lockedOpen = hasPrepay; // an active prepayment keeps the section pinned open
  const effectiveOpen = lockedOpen || open;

  const addLumpSum = () => {
    if (!amount || !month) return;
    setPrepayments([...prepayments, { id: Date.now(), month: Number(month), amount: Number(amount) }]);
  };

  const removeItem = (id) => setPrepayments(prepayments.filter((p) => p.id !== id));

  const summary = hasPrepay
    ? `${prepayments.length} lump-sum${prepayments.length === 1 ? '' : 's'}\u00A0\u00B7 +${formatMoneyShort(extraEmi, currency)}/mo`
    : t('prepayment_desc');

  return (
    <section className="emi-wrap mt-8">
      <div className="rounded-2xl border border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#101B33] overflow-hidden">
        <button
          type="button"
          onClick={() => { if (!lockedOpen) setOpen((v) => !v); }}
          className={`w-full flex items-center justify-between p-4 transition-colors ${lockedOpen ? 'cursor-default' : 'hover:bg-slate-50/60 dark:hover:bg-[#1B2743]/60'}`}
          aria-expanded={effectiveOpen}
        >
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-[#ECFDF5] text-[#059669] flex items-center justify-center">
              <PiggyBank className="h-4 w-4" />
            </div>
            <div className="text-left">
              <div className="text-base font-semibold text-[#032D60] dark:text-slate-100">{t('prepayment_title')}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 max-w-2xl truncate">{summary}</div>
            </div>
          </div>
          {lockedOpen ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ECFDF5] dark:bg-[#0B2A1D] border border-[#A7F3D0] dark:border-[#0F5D42] px-2.5 py-1 text-[10px] font-bold text-[#059669] dark:text-emerald-300 uppercase tracking-wide shrink-0" data-testid="prepayment-active-badge">
              Active
            </span>
          ) : (
            <ChevronDown className={`h-5 w-5 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
          )}
        </button>

        <div className="overflow-hidden transition-all" style={{ maxHeight: effectiveOpen ? '3000px' : '0px' }}>
          <div className="border-t border-slate-100 dark:border-[#1E2A44] p-4 sm:p-5 space-y-4">

            {/* Compact 3-stat row */}
            <div className="grid grid-cols-3 gap-2">
              <StatChip label={t('interest_saved')} value={formatMoneyShort(interestSaved, currency)} tone="green" />
              <StatChip label={t('tenure_shortened')} value={monthsSaved > 0 ? monthsToLabel(monthsSaved) : '\u2014'} tone="sky" />
              <StatChip label={t('new_payoff_time')} value={monthsToLabel(current?.monthsTaken || 0)} tone="blue" />
            </div>

            {/* Inputs — 2 columns side-by-side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Extra monthly */}
              <div className="rounded-lg border border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#0F1B36] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-3.5 w-3.5 text-[#D97706]" />
                  <div className="text-xs font-bold text-[#032D60] dark:text-slate-100">{t('extra_every_month')}</div>
                </div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-2 leading-snug">{t('extra_every_month_desc')}</p>
                <div className="flex items-center rounded-md border border-slate-200 dark:border-[#1E2A44] focus-within:border-[#0176D3] focus-within:ring-2 focus-within:ring-[#B7E1FA]">
                  <span className="px-2.5 text-slate-500 dark:text-slate-400 text-xs">{c.symbol}</span>
                  <input
                    type="number"
                    min={0}
                    value={extraEmi}
                    onChange={(e) => setExtraEmi(Math.max(0, Number(e.target.value) || 0))}
                    className="flex-1 bg-transparent py-1.5 pr-2 outline-none text-sm tabular-nums text-[#032D60] dark:text-white"
                    placeholder="0"
                  />
                </div>
                <div className="mt-2 flex gap-1 flex-wrap">
                  {[0, 1000, 5000, 10000, 50000, 100000].map((v) => (
                    <button
                      key={v}
                      onClick={() => setExtraEmi(v)}
                      className={`btn-3d px-2 py-0.5 rounded-md text-[10px] font-semibold border transition-all ${
                        extraEmi === v
                          ? 'bg-gradient-to-b from-[#0EA5FF] to-[#0176D3] border-[#0176D3] text-white'
                          : 'bg-white dark:bg-[#0F1B36] border-slate-200 dark:border-[#1E2A44] text-slate-600 dark:text-slate-400 hover:border-[#0176D3] hover:text-[#0176D3]'
                      }`}
                      data-testid={`extra-quick-${v}`}
                    >
                      {v === 0 ? t('none') : `+${formatMoneyShort(v, currency)}`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Lump-sum */}
              <div className="rounded-lg border border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#0F1B36] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <PiggyBank className="h-3.5 w-3.5 text-[#059669]" />
                  <div className="text-xs font-bold text-[#032D60] dark:text-slate-100">{t('lump_sum_title')}</div>
                </div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-2 leading-snug">{t('lump_sum_desc')}</p>
                <div className="flex gap-1.5">
                  <div className="flex items-center rounded-md border border-slate-200 dark:border-[#1E2A44] focus-within:border-[#0176D3] focus-within:ring-2 focus-within:ring-[#B7E1FA] flex-1 min-w-0">
                    <span className="px-2 text-slate-500 dark:text-slate-400 text-xs">{c.symbol}</span>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value) || 0)}
                      className="w-full bg-transparent py-1.5 pr-2 outline-none text-sm tabular-nums text-[#032D60] dark:text-white"
                      placeholder="50000"
                    />
                  </div>
                  <div className="flex items-center rounded-md border border-slate-200 dark:border-[#1E2A44] focus-within:border-[#0176D3] focus-within:ring-2 focus-within:ring-[#B7E1FA] shrink-0">
                    <span className="px-2 text-slate-500 dark:text-slate-400 text-xs">{t('month')}</span>
                    <input
                      type="number"
                      min={1}
                      max={maxMonth}
                      value={month}
                      onChange={(e) => setMonth(Number(e.target.value) || 1)}
                      className="w-14 bg-transparent py-1.5 pr-2 outline-none text-sm tabular-nums text-[#032D60] dark:text-white"
                    />
                  </div>
                  <button
                    onClick={addLumpSum}
                    className="btn-3d h-8 px-2.5 rounded-md bg-gradient-to-b from-[#0EA5FF] to-[#0176D3] hover:from-[#38BDF8] hover:to-[#014486] text-white text-xs font-semibold flex items-center gap-1 shrink-0"
                    data-testid="add-lumpsum-btn"
                  >
                    <Plus className="h-3 w-3" /> {t('add')}
                  </button>
                </div>
                {prepayments.length > 0 && (
                  <ul className="mt-2 space-y-1 max-h-24 overflow-auto">
                    {prepayments.map((p) => (
                      <li key={p.id} className="flex items-center justify-between rounded-md bg-[#ECFDF5] dark:bg-[#0B2A1D] border border-[#A7F3D0] dark:border-[#0F5D42] px-2 py-1">
                        <div className="text-[11px] text-[#065F46] dark:text-emerald-300 truncate">
                          <span className="font-semibold tabular-nums">{formatMoney(p.amount, currency)}</span>
                          <span className="text-[10px] text-[#047857] dark:text-emerald-400 ml-1.5">{t('at_month')} {p.month}</span>
                        </div>
                        <button onClick={() => removeItem(p.id)} className="text-[#065F46] dark:text-emerald-300 hover:text-red-600 shrink-0">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Two side-by-side mini breakup charts (only when user has any prepay) */}
            {hasPrepay && (
              <div className="grid grid-cols-2 gap-3">
                <MiniChartCard
                  title="Without Prepayment"
                  subtitle="Baseline"
                  principal={loanAmount}
                  interest={baselineSummary.totalInterest || 0}
                  emi={baselineSummary.emi || 0}
                  roi={Number(baselineSummary.effectiveAnnualRate || 0)}
                  moratoriumInterest={moratoriumEnabled && moratoriumType === 'full' ? (baselineSummary.moratoriumInterestCapitalised || 0) : 0}
                  schedule={baseline?.schedule || []}
                  chartType={chartType}
                  chartId="prepay-actual-chart"
                  currency={currency}
                />
                <MiniChartCard
                  title="With Prepayment"
                  subtitle="Your plan"
                  principal={loanAmount}
                  interest={current?.totalInterest || 0}
                  emi={current?.emi || 0}
                  roi={Number(current?.effectiveAnnualRate || 0)}
                  moratoriumInterest={moratoriumEnabled && moratoriumType === 'full' ? (current?.moratoriumInterestCapitalised || 0) : 0}
                  schedule={currentFull?.schedule || []}
                  chartType={chartType}
                  chartId="prepay-current-chart"
                  currency={currency}
                  highlight
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatChip({ label, value, tone = 'blue' }) {
  const tones = {
    green: 'bg-gradient-to-br from-[#ECFDF5] to-[#D1FAE5] dark:from-[#0B2A1D] dark:to-[#10362A] border-[#A7F3D0] dark:border-[#0F5D42] text-[#065F46] dark:text-emerald-300',
    sky: 'bg-gradient-to-br from-[#EAF5FE] to-[#B7E1FA] dark:from-[#0F1B36] dark:to-[#1E3A5F] border-[#B7E1FA] dark:border-[#1E4A72] text-[#0176D3] dark:text-sky-300',
    blue: 'bg-gradient-to-br from-[#F0F9FF] to-[#E0F2FE] dark:from-[#0F1B36] dark:to-[#0B2540] border-[#7DD3FC] dark:border-[#0369A1] text-[#0369A1] dark:text-sky-300',
  }[tone];
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones}`}>
      <div className="text-[9px] uppercase tracking-wide font-bold opacity-80">{label}</div>
      <div className="mt-0.5 text-base font-bold tabular-nums text-[#032D60] dark:text-white">{value}</div>
    </div>
  );
}
